// Динамически определяем базовый URL API на основе текущего хоста
const getApiBase = () => {
  const hostname = window.location.hostname;
  // В локальной разработке (localhost) обращаемся напрямую к бэкенду на порту 8080
  // В продакшне запросы идут через Nginx (proxy_pass /api/v1/ → localhost:8080)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const protocol = window.location.protocol;
    return `${protocol}//${hostname}:8080/api/v1`;
  }
  // Продакшн: используем тот же протокол и хост, без явного порта
  return `${window.location.protocol}//${window.location.host}/api/v1`;
};

const API_BASE = getApiBase();

class ApiService {
  constructor() {
    this.accessToken = null;
    this.refreshToken = null;
    this._refreshPromise = null; // защита от параллельных refresh-запросов
  }

  // ===== Управление токенами =====

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  loadRefreshToken() {
    this.refreshToken = localStorage.getItem('refreshToken') || null;
  }

  getAuthHeader() {
    return this.accessToken
      ? { Authorization: `Bearer ${this.accessToken}` }
      : {};
  }

  // ===== Обновление access-токена =====

  async _doRefresh() {
    if (!this.refreshToken) {
      throw new Error('Нет refresh-токена');
    }
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: this.refreshToken }),
    });
    if (!response.ok) {
      this.clearTokens();
      throw new Error('Сессия истекла, войдите снова');
    }
    const data = await response.json();
    this.setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  }

  async refreshAccessToken() {
    // Если уже идёт refresh — ждём его, не запускаем второй
    if (!this._refreshPromise) {
      this._refreshPromise = this._doRefresh().finally(() => {
        this._refreshPromise = null;
      });
    }
    return this._refreshPromise;
  }

  // ===== Универсальный fetch с авто-обновлением токена =====

  async _fetch(url, options = {}) {
    const headers = {
      ...this.getAuthHeader(),
      ...options.headers,
    };

    let response = await fetch(url, { ...options, headers });

    // Если 401 — пробуем обновить токен и повторить запрос
    if (response.status === 401) {
      try {
        await this.refreshAccessToken();
        const retryHeaders = {
          ...this.getAuthHeader(),
          ...options.headers,
        };
        response = await fetch(url, { ...options, headers: retryHeaders });
      } catch {
        // refresh не удался — токены уже очищены в _doRefresh
        // Уведомляем приложение об истечении сессии
        window.dispatchEvent(new Event('session:expired'));
        throw new Error('SESSION_EXPIRED');
      }
    }

    return response;
  }

  // ===== Аутентификация =====

  async login(credentials) {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });
    return response.json();
  }

  async logout() {
    if (this.refreshToken) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: {
            ...this.getAuthHeader(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: this.refreshToken }),
        });
      } catch {
        // Игнорируем ошибки при logout — всё равно чистим локальное состояние
      }
    }
    this.clearTokens();
  }

  // ===== Пользователи =====

  async getUsers() {
    const response = await this._fetch(`${API_BASE}/users`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async createUser(userData) {
    const response = await this._fetch(`${API_BASE}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async deleteUser(userId) {
    const response = await this._fetch(`${API_BASE}/users/${userId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getLogs() {
    const response = await this._fetch(`${API_BASE}/logs`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // ===== Лицевые счета =====

  async getAccounts() {
    const response = await this._fetch(`${API_BASE}/accounts`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async createAccount(accountData) {
    const response = await this._fetch(`${API_BASE}/accounts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(accountData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async updateAccount(accountNumber, accountData) {
    const response = await this._fetch(`${API_BASE}/accounts/${accountNumber}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(accountData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async deleteAccount(accountNumber) {
    const response = await this._fetch(`${API_BASE}/accounts/${accountNumber}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async uploadAccounts(formData) {
    const response = await this._fetch(`${API_BASE}/accounts/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getAccountsWithPagination(params) {
    const queryString = params.toString();
    const url = `${API_BASE}/accounts${queryString ? `?${queryString}` : ''}`;
    const response = await this._fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // ===== Приказы =====

  async getOrders(queryParams = '') {
    const url = queryParams
      ? `${API_BASE}/orders?${queryParams}`
      : `${API_BASE}/orders`;
    const response = await this._fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async createOrder(orderData) {
    const response = await this._fetch(`${API_BASE}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async updateOrder(orderId, orderData) {
    const response = await this._fetch(`${API_BASE}/orders/${orderId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async deleteOrder(orderId) {
    const response = await this._fetch(`${API_BASE}/orders/${orderId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async uploadOrders(formData) {
    const response = await this._fetch(`${API_BASE}/orders/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // ===== Выписки =====

  async uploadStatements(formData) {
    const response = await this._fetch(`${API_BASE}/statements/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async uploadCorrection(formData) {
    const response = await this._fetch(`${API_BASE}/correction/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getAccountByNumber(number) {
    const response = await this._fetch(`${API_BASE}/accounts/${number}`);
    return response.json();
  }

  async getOrdersByAccount(accountNumber, page = 1, pageSize = 25) {
    const response = await this._fetch(
      `${API_BASE}/accounts/${accountNumber}/orders?page=${page}&pageSize=${pageSize}`
    );
    return response.json();
  }

  async getOrderById(orderId) {
    const response = await this._fetch(`${API_BASE}/orders/${orderId}`);
    return response.json();
  }

  async getStatementsByOrderId(orderId, page = 1, pageSize = 25) {
    const response = await this._fetch(
      `${API_BASE}/orders/${orderId}/statements?page=${page}&pageSize=${pageSize}`
    );
    return response.json();
  }

  async getStatementsUploadedWithOrders(page = 1, pageSize = 25) {
    const response = await this._fetch(
      `${API_BASE}/statementsuploaded?page=${page}&pageSize=${pageSize}`
    );
    return response.json();
  }

  async getStatementsUnallocated(page = 1, pageSize = 25) {
    const response = await this._fetch(
      `${API_BASE}/statementsunallocated?page=${page}&pageSize=${pageSize}`
    );
    return response.json();
  }

  async linkStatementToOrder(statementId, orderId) {
    const response = await this._fetch(
      `${API_BASE}/statements/${statementId}/setorder/${orderId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // ===== Детализация =====

  async getDetails(params) {
    const queryString = params.toString();
    const url = `${API_BASE}/details${queryString ? `?${queryString}` : ''}`;
    const response = await this._fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async recalculateDetails() {
    const response = await this._fetch(`${API_BASE}/details/recalculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async saveUploadedStatementsToDB() {
    const response = await this._fetch(
      `${API_BASE}/statements/uploaded_savetodb`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async deleteStatement(statementId) {
    const response = await this._fetch(
      `${API_BASE}/statements/uploaded/${statementId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async deleteStatementSaved(statementId) {
    const response = await this._fetch(
      `${API_BASE}/statements/saved/${statementId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async unlinkStatementFromOrder(statementId) {
    const response = await this._fetch(
      `${API_BASE}/statements/${statementId}/setorder/0`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getDetailsSummaryByAccount(accountNumber) {
    const response = await this._fetch(
      `${API_BASE}/details/${accountNumber}/summary`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getDetailsByOrder(orderId) {
    const response = await this._fetch(`${API_BASE}/details/order/${orderId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getDetailsByAccount(accountNumber) {
    const response = await this._fetch(`${API_BASE}/details/${accountNumber}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async searchAccountNumbers(searchQuery) {
    const response = await this._fetch(
      `${API_BASE}/accounts/search?query=${encodeURIComponent(searchQuery)}`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getOwnersByAccountNumber(accountNumber) {
    const response = await this._fetch(
      `${API_BASE}/accounts/${accountNumber}/owners`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getStatementsSummaryByOrder(orderId) {
    const response = await this._fetch(
      `${API_BASE}/orders/${orderId}/statementssummary`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getStatementsSheets(formData) {
    const response = await this._fetch(
      `${API_BASE}/statements/uploadall/getsheets`,
      { method: 'POST', body: formData }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async loadStatementsFile(data) {
    const response = await this._fetch(
      `${API_BASE}/statements/uploadall/loadfile`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // ===== Резервные копии =====

  async getBackups() {
    const response = await this._fetch(`${API_BASE}/backups`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async createBackup() {
    const response = await this._fetch(`${API_BASE}/backups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async restoreBackup(filename) {
    const response = await this._fetch(
      `${API_BASE}/backups/${encodeURIComponent(filename)}/restore`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async deleteBackup(filename) {
    const response = await this._fetch(
      `${API_BASE}/backups/${encodeURIComponent(filename)}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async deleteAllStatementsAllocated() {
    const response = await this._fetch(`${API_BASE}/statementsallocated`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // ===== Журнал изменений =====

  async getChangelog() {
    const response = await fetch(`${API_BASE}/changelog`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.text();
  }

  // ===== Экспорт =====

  // Удаление всех неопределённых выписок
  async deleteAllStatementsUnallocated() {
    const response = await this._fetch(`${API_BASE}/statementsunallocated`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // Выгрузка неопределённых выписок в Excel
  async exportStatementsUnallocated() {
    const response = await this._fetch(`${API_BASE}/statementsunallocated/export`, {
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response; // возвращаем сырой Response для получения blob
  }

  // Выгрузка для БРИС за период (файл отдаётся как blob)
  async exportDetailsByPeriod(dateFrom, dateTo) {
    const response = await this._fetch(`${API_BASE}/details/export/period`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dateFrom, dateTo }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response; // возвращаем сырой Response для получения blob
  }

  // Отчёт ПИР за период (файл отдаётся как blob)
  async exportOrdersByDateReceiptPeriod(dateFrom, dateTo) {
    const response = await this._fetch(`${API_BASE}/orders/export/period`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dateFrom, dateTo }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response; // возвращаем сырой Response для получения blob
  }
}


const apiService = new ApiService();
export default apiService;
